import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { Button } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";

import Modal from "components/common/Modal";
import TransactionSuccessModal from "components/common/TransactionSuccessModal";
import TransactionFailureModal from "components/common/TransactionFailureModal";
import ShareModal from "components/profile/ShareModal";

import { payPaltelBillApiCalled, payBnetBillApiCalled } from "shared/endPoints/apiCallers";
import { Box, TextField, Paper, Typography } from "@mui/material";
import { paltelImage, Bnet as BnetLogo } from "assests/images/bundles/paltel/paltelImage";
import { noBalanceContext } from "reducers/no_balance";
import translate from "i18n/translate";
import ApiRequest from "../../actions/ApiRequest";

const normalizeYearMonthCandidate = (candidate) => {
  if (candidate === null || candidate === undefined) {
    return null;
  }

  if (typeof candidate === "string") {
    const trimmed = candidate.trim();
    if (!trimmed.length) {
      return null;
    }

    // Try to parse month name formats like "Oct-2025", "October-2025", "Nov 2025"
    const monthNamePattern = /^([A-Za-z]+)[\s-](\d{4})$/;
    const match = trimmed.match(monthNamePattern);
    if (match) {
      const monthStr = match[1].toLowerCase();
      const year = match[2];

      // Map month names to numbers
      const monthMap = {
        'jan': '01', 'january': '01',
        'feb': '02', 'february': '02',
        'mar': '03', 'march': '03',
        'apr': '04', 'april': '04',
        'may': '05',
        'jun': '06', 'june': '06',
        'jul': '07', 'july': '07',
        'aug': '08', 'august': '08',
        'sep': '09', 'sept': '09', 'september': '09',
        'oct': '10', 'october': '10',
        'nov': '11', 'november': '11',
        'dec': '12', 'december': '12'
      };

      const monthNum = monthMap[monthStr];
      if (monthNum) {
        return `${year}-${monthNum}`;
      }
    }

    return trimmed;
  }

  if (typeof candidate === "number") {
    const numeric = candidate.toString();
    return numeric.length ? numeric : null;
  }

  return null;
};

const resolvePaltelYearMonth = (bill) => {
  if (!bill || typeof bill !== "object") {
    return null;
  }

  const candidates = [
    bill.year_month,
    bill.yearMonth,
    bill.date,
    bill.date1,
    bill.cycle,
    bill.billCycle,
    bill.billingPeriod,
    bill.billing_period,
    bill?.userBill?.yearMonth,
    bill?.userBill?.billCycle,
    bill?.userBill?.billingPeriod,
    bill?.userBill?.billing_period,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeYearMonthCandidate(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return null;
};

const extractNumericAmount = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "number" && !Number.isNaN(value)) {
    return value.toString();
  }

  const trimmed = String(value).trim();
  if (!trimmed) {
    return null;
  }

  const match = trimmed.match(/[\d.,]+/);
  if (!match) {
    return null;
  }

  let numericStr = match[0];
  const hasComma = numericStr.includes(",");
  const hasDot = numericStr.includes(".");

  if (hasComma && hasDot) {
    if (numericStr.lastIndexOf(".") > numericStr.lastIndexOf(",")) {
      numericStr = numericStr.replace(/,/g, "");
    } else {
      numericStr = numericStr.replace(/\./g, "").replace(/,/g, ".");
    }
  } else if (hasComma) {
    numericStr = numericStr.replace(/,/g, ".");
  }

  const numeric = parseFloat(numericStr);
  if (Number.isNaN(numeric)) {
    return null;
  }

  return numeric.toString();
};

const resolvePaltelBillAmountValue = (bill) => {
  if (!bill || typeof bill !== "object") {
    return null;
  }

  const amountCandidates = [
    bill.amount,
    bill.must_paid,
    bill.mustPay,
    bill.must_paid_value,
    bill.mustPaid,
    bill.total_amount,
    bill.totalAmount,
    bill.total,
    bill.pay_amount,
    bill.payAmount,
    bill.balance,
  ];

  for (const candidate of amountCandidates) {
    const resolved = extractNumericAmount(candidate);
    if (resolved) {
      return resolved;
    }
  }

  return null;
};

const normalizeReasonMessage = (reason) => {
  if (!reason) {
    return "";
  }

  if (typeof reason === "string") {
    return reason.trim();
  }

  if (typeof reason === "object") {
    const candidate =
      reason.message ??
      reason.reason ??
      reason.status ??
      reason.description ??
      reason.text;

    if (candidate && typeof candidate === "string") {
      return candidate.trim();
    }

    try {
      const serialized = JSON.stringify(reason);
      return serialized && serialized !== "{}" ? serialized : "";
    } catch (error) {
      return "";
    }
  }

  return String(reason);
};

const PayBillModal = ({
  number,
  selectedBill,
  isDisabled,
  clearSelections,
  provider = "paltel",
}) => {
  const noBalanceCtx = useContext(noBalanceContext);
  const intl = useIntl();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState("manual");
  const [identityValue, setIdentityValue] = useState("");
  const [isAutoIdCheckLoading, setIsAutoIdCheckLoading] = useState(false);
  const [isAutoIdRequired, setIsAutoIdRequired] = useState(false);
  const [autoIdCheckError, setAutoIdCheckError] = useState("");
  const history = useHistory();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failureMessage, setFailureMessage] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [transactionData, setTransactionData] = useState(null);
  const [shareContent, setShareContent] = useState("");

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
    if (mode === "manual") {
      setIdentityValue("");
      setIsAutoIdRequired(false);
      setAutoIdCheckError("");
      setIsAutoIdCheckLoading(false);
    } else if (mode === "auto" && !number) {
      setIsAutoIdRequired(true);
    }
  };

  const checkPaltelIdRequirement = useCallback(async () => {
    if (!number || selectedMode !== "auto") {
      return;
    }
    setIsAutoIdCheckLoading(true);
    setAutoIdCheckError("");
    try {
      const response = await ApiRequest.post(`/is_id_required?number=${number}`);
      const data = response?.data ?? response;
      if (data?.status === "success") {
        const requires =
          data.requires_id === true ||
          data.requires_id === "true" ||
          data.requires_id === 1;
        setIsAutoIdRequired(requires);
        if (data.id_number && data.id_number.trim()) {
          setIdentityValue(data.id_number.trim());
        } else if (!requires) {
          setIdentityValue("");
        }
      } else {
        setIsAutoIdRequired(true);
        setAutoIdCheckError(
          data?.error ||
          data?.reason ||
          "Unable to verify ID requirement. Please enter the national ID."
        );
      }
    } catch (error) {
      setIsAutoIdRequired(true);
      setAutoIdCheckError(
        error?.response?.data?.error ||
        "Unable to verify ID requirement. Please enter the national ID."
      );
    } finally {
      setIsAutoIdCheckLoading(false);
    }
  }, [number, selectedMode]);

  useEffect(() => {
    if (selectedMode === "auto") {
      checkPaltelIdRequirement();
    }
  }, [selectedMode, number, checkPaltelIdRequirement]);

  const { mutateAsync: payBill, isLoading } = useMutation(
    provider === "bnet" ? payBnetBillApiCalled : payPaltelBillApiCalled,
    {
      onError: (error) => {
        const message =
          error?.response?.data?.reason ||
          error?.message ||
          translate("Transaction Failed");
        setFailureMessage(message);
        setShowFailureModal(true);
        setIsOpen(false);
      },
    }
  );

  const handleSuccessModalClose = () => {
    setShowShareModal(false);
    setShareContent("");
    setTransactionData(null);
    setShowSuccessModal(false);
    setSuccessMessage("");
    clearSelections();
    setSelectedMode("manual");
    setIdentityValue("");
    setIsAutoIdRequired(false);
    setAutoIdCheckError("");
    setIsAutoIdCheckLoading(false);
    history.push("/pay_bills");
  };

  const handleFailureModalClose = () => {
    setShowFailureModal(false);
    setFailureMessage("");
    clearSelections();
    setSelectedMode("manual");
    setIdentityValue("");
    setIsAutoIdRequired(false);
    setAutoIdCheckError("");
    setIsAutoIdCheckLoading(false);
  };

  const resolvedYearMonth = useMemo(
    () => resolvePaltelYearMonth(selectedBill),
    [selectedBill]
  );

  const createShareMessage = (summary) => {
    if (!summary) {
      return "";
    }

    const {
      status,
      reason,
      transactionId,
      number: payerNumber,
      amount,
      paymentMode: mode,
      identity: idValue,
      yearMonth,
      bill,
      timestamp,
    } = summary;

    const billLines = [];
    if (bill && typeof bill === "object") {
      const billNumber =
        bill.bill_number ??
        bill.billNumber ??
        bill.invoiceNumber ??
        bill.accountNumber ??
        bill.id ??
        "";
      const billAmount = bill.amount ?? bill.pay_amount ?? bill.must_paid ?? "";
      const billYearMonth =
        yearMonth ??
        bill.year_month ??
        bill.yearMonth ??
        bill.billingPeriod ??
        bill.billing_period ??
        "";
      billLines.push(
        `Bill: ${billNumber || "N/A"} | Amount: ${billAmount || "N/A"} | Cycle: ${billYearMonth || "N/A"
        }`
      );
    }

    const printedAt = new Date(timestamp || Date.now()).toLocaleString();
    const providerName = provider === "bnet" ? "Bnet" : "Paltel";

    return `${providerName} Bill Payment Receipt

Status: ${status || "success"}
Reason: ${reason || "Payment processed successfully!"}
Transaction ID: ${transactionId || "N/A"}
Phone Number: ${payerNumber || "N/A"}
Amount Paid: ${amount || "N/A"} NIS
Payment Mode: ${mode || "auto"}
Customer ID: ${idValue || "N/A"}
Billing Period: ${yearMonth || "N/A"}

Bill Details:
${billLines.length ? billLines.join("\n") : "N/A"}

Date: ${printedAt}`;
  };

  const handleShareClick = () => {
    if (!transactionData) {
      return;
    }
    const content = shareContent || createShareMessage(transactionData);
    setShareContent(content);
    setShowShareModal(true);
  };

  const handlePrintClick = () => {
    if (!transactionData) {
      return;
    }
    const {
      status,
      reason,
      transactionId,
      number: payerNumber,
      amount,
      paymentMode: mode,
      identity: idValue,
      yearMonth,
      bill,
      timestamp,
    } = transactionData;

    const printedAt = new Date(timestamp || Date.now()).toLocaleString();
    let billDetailsHtml = "<p>N/A</p>";
    if (bill && typeof bill === "object") {
      const billNumber =
        bill.bill_number ??
        bill.billNumber ??
        bill.invoiceNumber ??
        bill.accountNumber ??
        bill.id ??
        "N/A";
      const billAmount = bill.amount ?? bill.pay_amount ?? bill.must_paid ?? "N/A";
      const billYearMonth =
        yearMonth ??
        bill.year_month ??
        bill.yearMonth ??
        bill.billingPeriod ??
        bill.billing_period ??
        "N/A";
      billDetailsHtml = `
        <div class="bill-details">
          <div><span class="label">Bill Number:</span> ${billNumber}</div>
          <div><span class="label">Bill Amount:</span> ${billAmount}</div>
          <div><span class="label">Cycle:</span> ${billYearMonth}</div>
        </div>
      `;
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      return;
    }
    const providerName = provider === "bnet" ? "Bnet" : "Paltel";
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>${providerName} Bill Payment Receipt</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; direction: ltr; }
            .header { font-size: 18px; font-weight: bold; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .section { margin-bottom: 15px; }
            .label { font-weight: bold; }
            .bill-details { background-color: #f5f5f5; padding: 10px; margin: 10px 0; border-radius: 5px; }
            .footer { margin-top: 30px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">${providerName} Bill Payment Receipt</div>
          <div class="section">
            <div><span class="label">Status:</span> ${status || "success"}</div>
            <div><span class="label">Reason:</span> ${reason || translate("paltel_payment_successfully_processed")}</div>
            <div><span class="label">Transaction ID:</span> ${transactionId || "N/A"}</div>
            <div><span class="label">Phone Number:</span> ${payerNumber || "N/A"}</div>
            <div><span class="label">Amount Paid:</span> ${amount || "N/A"} NIS</div>
            <div><span class="label">Payment Mode:</span> ${mode || "auto"}</div>
            <div><span class="label">Customer ID:</span> ${idValue || "N/A"}</div>
            <div><span class="label">Billing Period:</span> ${yearMonth || "N/A"}</div>
          </div>
          <div class="section">
            <span class="label">Bill Details:</span>
            ${billDetailsHtml}
          </div>
          <div class="footer">
            Printed on: ${printedAt}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    console.log("PayBillModal onSubmit selectedBill:", selectedBill);
    console.log("PayBillModal onSubmit resolvedYearMonth:", resolvedYearMonth);
    const sanitizedNumber =
      typeof number === "string" ? number.trim() : number;
    if (!sanitizedNumber) {
      setFailureMessage(translate("Transaction Failed"));
      setShowFailureModal(true);
      return;
    }

    const resolvedAmountValue = resolvePaltelBillAmountValue(selectedBill);
    if (!resolvedAmountValue) {
      setFailureMessage(translate("paltel_unable_to_resolve_amount"));
      setShowFailureModal(true);
      return;
    }

    const payload = {
      number: sanitizedNumber,
      amount: resolvedAmountValue,
      wNumber: "N/A",
      email: "N/A",
    };
    if (selectedMode) {
      payload.paymentMode = selectedMode;
    }
    if (selectedBill?.id) {
      payload.billId = selectedBill.id;
    }

    // Add year_month for both manual and auto modes (required for Paltel Fiber)
    let yearMonthToSend = resolvedYearMonth;

    // Fallback: if year_month is not in bill data, use current year-month for service numbers
    if (!yearMonthToSend && sanitizedNumber && sanitizedNumber.length >= 5 && sanitizedNumber.length <= 6) {
      // This is likely a Paltel Fiber service number (5-6 digits)
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      yearMonthToSend = `${year}-${month}`;
      console.warn(`Using current year-month as fallback for service number ${sanitizedNumber}: ${yearMonthToSend}`);
    }

    if (yearMonthToSend) {
      payload.yearMonth = yearMonthToSend;  // Use yearMonth (camelCase) to match API caller parameter
    }

    if (selectedMode === "auto") {
      if (isAutoIdRequired) {
        if (!identityValue || !identityValue.trim()) {
          setFailureMessage(translate("paltel_auto_id_required"));
          setShowFailureModal(true);
          return;
        }
        payload.identity = identityValue.trim();
      } else if (identityValue && identityValue.trim()) {
        payload.identity = identityValue.trim();
      }
      payload.billDetails = selectedBill;
    }

    try {
      const response = await payBill(payload);

      if (response?.reason === "seller no balamce/ disabled") {
        noBalanceCtx.openCardHandler();
        setFailureMessage(
          response?.reason || translate("Transaction Failed")
        );
        setShowFailureModal(true);
        setIsOpen(false);
        return;
      }

      // For manual payments, "pending" status means successfully sent (pending verification)
      const isManualPayment = selectedMode === "manual";
      const isPendingStatus = response?.status === "pending" || response?.status === "Pending";

      if (response?.status === "failed" && !isPendingStatus) {
        setFailureMessage(
          response?.reason || translate("Transaction Failed")
        );
        setShowFailureModal(true);
        return;
      }

      // Treat pending as success for manual payments, or actual success status
      if (response?.status === "success" || !response?.status || (isManualPayment && isPendingStatus)) {
        // Ensure reason is a string
        const reasonStr = normalizeReasonMessage(response?.reason);

        const successCopy = intl.formatMessage({ id: "paltel_payment_successfully_processed" });
        const transactionIdLabel = intl.formatMessage({ id: "transactionId" });

        // For manual payments with pending status, show success message
        let baseMessage;
        if (isManualPayment && isPendingStatus) {
          baseMessage = successCopy;
        } else {
          baseMessage =
            reasonStr && reasonStr.toLowerCase() !== "success"
              ? reasonStr
              : successCopy;
        }

        // Ensure transaction ID is a string
        const derivedTransactionId =
          response?.trans_id ??
          response?.transaction_id ??
          response?.transid ??
          null;

        const transactionIdStr = derivedTransactionId
          ? (typeof derivedTransactionId === "string" ? derivedTransactionId : String(derivedTransactionId))
          : null;

        const finalMessage = transactionIdStr
          ? `${baseMessage}\n${transactionIdLabel}: ${transactionIdStr}`
          : baseMessage;

        const transactionSummary = {
          status: response?.status ?? "success",
          reason: reasonStr || baseMessage,
          transactionId: transactionIdStr,
          number: sanitizedNumber,
          amount: resolvedAmountValue,
          paymentMode: selectedMode,
          identity: selectedMode === "auto" ? identityValue.trim() : null,
          yearMonth: resolvedYearMonth,
          bill: selectedBill,
          timestamp: new Date().toISOString(),
        };

        setSuccessMessage(finalMessage);
        setIsOpen(false);
        setShowSuccessModal(true);
        setTransactionData(transactionSummary);
        setShareContent(createShareMessage(transactionSummary));

        return;
      }

      if (response?.reason) {
        setFailureMessage(response.reason);
        setShowFailureModal(true);
        setIsOpen(false);
        return;
      }
    } catch (error) {
      const message =
        error?.response?.data?.reason ||
        error?.message ||
        "Something went wrong!";
      setFailureMessage(message);
      setShowFailureModal(true);
      setIsOpen(false);
    }
  };

  return (
    <div id="paltel-pay-bills-modal-div">
      <Button
        onClick={() => {
          if (!isDisabled) {
            setSelectedMode("manual");
            setIdentityValue("");
            setIsOpen(true);
          }
        }}
        id="paltel-pay-bills-pay-btn"
        disabled={isDisabled}
      >
        {translate("Pay")}
      </Button>
      {isOpen && (
        <Modal
          visible={isOpen}
          onClose={() => setIsOpen(false)}
          title={"Pay"}
          width={"min(420px, 90vw)"}
          id="paltel-pay-bills-modal"
          onSuccess={onSubmit}
          onSuccessLoading={isLoading}
        >
          <form className="phone-form" id="paltel-bills-modal-form">
            <Box
              sx={{
                maxWidth: 420,
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={provider === "bnet" ? BnetLogo : paltelImage}
                  alt={provider === "bnet" ? "Bnet" : "Paltel"}
                  sx={{
                    width: 140,
                    height: "auto",
                    borderRadius: 1.5,
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#f8fafc",
                    borderRadius: 1.5,
                    padding: 1.2,
                    border: "1px solid rgba(148, 163, 184, 0.35)",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: "uppercase",
                      color: "#64748b",
                      letterSpacing: 0.6,
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  >
                    {translate("Number")}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#0f172a", mt: 0.3, fontSize: "1.1rem" }}
                  >
                    {number}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%)",
                    borderRadius: 1.5,
                    padding: 1.2,
                    textAlign: "center",
                    color: "#fff",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.8)",
                      letterSpacing: 0.6,
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  >
                    {translate("money")}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, mt: 0.3, fontSize: "1.3rem" }}
                  >
                    {selectedBill?.amount}{" "}
                    <Typography component="span" variant="caption" sx={{ color: "rgba(255,255,255,0.8)", ml: 0.3, fontSize: "0.7rem" }}>
                      ILS
                    </Typography>
                  </Typography>
                </Box>
              </Box>
              {selectedMode === "auto" && (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.3,
                    mt: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    color={isAutoIdCheckLoading ? "text.secondary" : isAutoIdRequired ? "text.secondary" : "success.main"}
                    sx={{ fontSize: "0.7rem", fontWeight: !isAutoIdRequired && !isAutoIdCheckLoading ? 600 : 400 }}
                  >
                    {isAutoIdCheckLoading
                      ? "Checking ID requirement…"
                      : isAutoIdRequired
                        ? translate("paltel_auto_id_required")
                        : "Trusted number detected — national ID is not required."}
                  </Typography>
                  {autoIdCheckError && (
                    <Typography variant="caption" color="error" sx={{ fontSize: "0.65rem" }}>
                      {autoIdCheckError}
                    </Typography>
                  )}
                </Box>
              )}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.85rem" }}>
                  {translate("paltel_select_payment_mode")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  {["manual", "auto"].map((mode) => {
                    const isActive = selectedMode === mode;
                    const title =
                      mode === "manual"
                        ? translate("paltel_manual_title")
                        : translate("paltel_auto_title");
                    const description =
                      mode === "manual"
                        ? translate("paltel_manual_info")
                        : translate("paltel_auto_info");
                    return (
                      <Paper
                        key={mode}
                        onClick={() => handleModeSelect(mode)}
                        sx={{
                          cursor: "pointer",
                          padding: 1.2,
                          borderRadius: 1.5,
                          flex: "1 1 0",
                          border: isActive
                            ? "2px solid #1976d2"
                            : "1px solid rgba(0,0,0,0.12)",
                          backgroundColor: isActive
                            ? "rgba(25, 118, 210, 0.08)"
                            : "#fff",
                          transition: "all 0.2s ease-in-out",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mb: 0.5, fontSize: "0.85rem" }}
                        >
                          {title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                          {description}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
              {selectedMode === "auto" && isAutoIdRequired && (
                <Box sx={{ width: "100%", mt: -0.5 }}>
                  <TextField
                    value={identityValue}
                    type="text"
                    id="paltel-pay-bills-id-input-field"
                    onChange={(e) => setIdentityValue(e.target.value)}
                    label={translate("paltel_auto_id_label")}
                    sx={{ width: "100%" }}
                    size="small"
                  />
                </Box>
              )}
            </Box>
          </form>
        </Modal>
      )}
      <TransactionSuccessModal
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
        message={
          successMessage || translate(provider === "bnet" ? "bnet_payment_successfully_processed" : "paltel_payment_successfully_processed")
        }
        showShareButton={transactionData?.paymentMode === "auto"}
        showPrintButton={transactionData?.paymentMode === "auto"}
        onShareClick={handleShareClick}
        onPrintClick={handlePrintClick}
      />
      <TransactionFailureModal
        open={showFailureModal}
        onClose={handleFailureModalClose}
        message={failureMessage || translate("Transaction Failed")}
      />
      <ShareModal
        shareModal={showShareModal}
        setShareModal={setShowShareModal}
        referalCode=""
        contentMessage={shareContent}
      />
    </div>
  );
};

export default PayBillModal;
